import { CLIP_MARKER_ATTRIBUTE } from "@seavien/commons";
import { getUserOwnedImageReferenceFromUrl } from "@/main/util/ImageUrlHelper";
import { mediaPermalink, type MediaPermalinkHint } from "@/main/util/MediaPermalink";

/**
 * After mount, wrap qualifying body `<img>` elements in a link to their framed permalink
 * (`/media/{imagePublicId}`), so clicking an image in a post opens it full-viewport with attribution
 * instead of doing nothing.
 *
 * <p><b>Why client-side.</b> Body images are rendered from stored HTML via `dangerouslySetInnerHTML`,
 * not from a React tree, so the alternatives were rewriting stored content at publish time (mutates
 * user content irreversibly and bakes a URL scheme into the corpus) or a server-side jsoup rewrite on
 * the render path (a full HTML parse on the busiest page in the product, for a link). This costs the
 * server nothing and never touches stored content.
 *
 * <p><b>It only ever ADDS.</b> The `<img>` node present before enhancement is the same node afterwards,
 * with a parent inserted around it — nothing is replaced, removed, or re-attributed. That satisfies
 * AGENTS.md rule 32 ("JS may only improve on the HTML we shipped") by construction: the worst case if
 * this function throws or never runs is exactly today's markup.
 *
 * <p><b>The second pass, added 2026-08-20, holds the same contract.</b>
 * `enhanceBodyImageFallbacks` gives a provably failed image the product's own placeholder as a
 * SIBLING and repairs the literal `alt="null"` a pre-`ec2a6a7a7` serializer left in stored bodies. It
 * runs from here rather than from its own call site because this is the seam the host already invokes
 * on every render; see its own doc comment for why neither half subtracts anything.
 *
 * <p>Media pages are `noindex`, so the usual objection to a JS-only link — crawlers cannot see it —
 * is not merely acceptable here, it is the intent.
 *
 * Design: designs/01-now/2026-07-25-framed-image-permalink-media-page-design.md §9.2
 */

/** Marks an anchor this module created. Diagnostics and tests only — skipping is driven by `closest("a")`. */
export const MEDIA_PERMALINK_ANCHOR_ATTRIBUTE = "data-media-permalink-anchor";

/** Set on an `<img>` (and on its `<figure>`, when it has one) whose asset provably failed to load. */
export const BODY_IMAGE_FAILED_ATTRIBUTE = "data-body-image-failed";

/** Marks the designed placeholder this module inserts beside a failed image. */
export const BODY_IMAGE_FALLBACK_ATTRIBUTE = "data-body-image-fallback";

/**
 * Internal: which `<img>` nodes are already wired to the error handler, so re-runs do not stack
 * listeners. A `WeakSet` and NOT a data attribute, deliberately — `bodyImagePermalinks.spec.ts`
 * asserts that a healthy image's markup is byte-identical after enhancement, which is this module's
 * whole contract restated as a test. Bookkeeping must not be visible in the DOM.
 */
const errorBoundImages = new WeakSet<HTMLImageElement>();

/** Internal: which images have already had a placeholder inserted. Same reason as above. */
const failedImages = new WeakSet<HTMLImageElement>();

/**
 * The four characters a pre-`ec2a6a7a7` body serializer wrote into every absent image attribute.
 * See `enhanceBodyImageFallbacks` for why this exact string is safe to treat as "absent".
 */
const LITERAL_NULL_ATTRIBUTE_VALUES = new Set(["null", "null/"]);

const BODY_IMAGE_FALLBACK_CLASS =
    "flex w-full flex-col items-center justify-center gap-2 rounded-xl border border-border bg-muted px-4 py-10 text-center";

/** The readable half of the placeholder. Flat-X: muted text, no second surface, no shadow. */
const BODY_IMAGE_FALLBACK_LABEL_CLASS = "text-sm text-neutral-text";

/**
 * A quiet placeholder mark. Flat-X: a border, no shadow, `--muted` fill, currentColor at low opacity.
 * Inline rather than a lucide import because this runs against injected DOM, not a React tree.
 */
const BODY_IMAGE_FALLBACK_GLYPH =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" '
    + 'stroke-linecap="round" stroke-linejoin="round" class="size-8 text-neutral-text opacity-60" '
    + 'aria-hidden="true" focusable="false">'
    + '<rect x="3" y="3" width="18" height="18" rx="2"/>'
    + '<circle cx="9" cy="9" r="1.5"/>'
    + '<path d="m21 15-4.5-4.5L9 18"/>'
    + '<path d="m3 3 18 18"/>'
    + '</svg>';

/**
 * Give a failed body image the product's own placeholder instead of the browser's broken-image chrome,
 * and stop the literal string {@code "null"} being announced as an image's name (`DISCUSSION-7`).
 *
 * <p><b>Two separate defects, one pass.</b> A body image that does not resolve renders the operating
 * system's torn-page glyph in a tall empty box, with the alt text painted as blue underlined link
 * text; and on every body published before `ec2a6a7a7` the alt text is the four characters `null`,
 * because the editor's serializer emitted `alt=null title=null width=null height=null` unquoted. An
 * empty alt is skipped by a screen reader; `null` is announced as the image's name.
 *
 * <p><b>Why the attribute repair is safe.</b> `LITERAL_NULL_ATTRIBUTE_VALUES` is not a heuristic about
 * what an author might have meant — nobody writes `null` as alt text, and `null/` is not even a
 * possible authored value: it exists only because the attributes were emitted unquoted and the
 * self-closing slash was swallowed into the last one. `alt` is set to `""` rather than removed, so the
 * image stays deliberately decorative instead of falling back to announcing its URL. This is a
 * render-time repair and does **not** substitute for the data backfill the row still owes.
 *
 * <p><b>Rule 32: this only ever ADDS, like the permalink pass above it.</b> The failed `<img>` is never
 * removed, replaced or re-parented — it keeps its `src`, it stays in the DOM, and the placeholder is
 * inserted as a SIBLING before it. The only thing suppressed is the paint of a box the browser has
 * already failed to fill; no server-rendered content, and nothing a crawler reads, is subtracted. If
 * this function never runs, throws, or a crawler renders before the `error` event fires, the page is
 * exactly today's markup.
 *
 * <p><b>Detection.</b> `complete && naturalWidth === 0` is a load that already failed — the common case
 * here, because this module re-runs on every render of the host and an image that failed on the first
 * pass will never fire `error` again. The listener covers everything still in flight. Both funnel into
 * one idempotent application.
 *
 * <p><b>The label, wired 2026-08-22 (wave 8, batch J).</b> `unavailableLabel` is the localized
 * `discussion.body.image_unavailable`, threaded down from `useBodyMediaLightbox` the same way
 * `enhanceBodyClipPlayers` already receives its localizer — this module has no React context of its
 * own. With the label present the placeholder carries readable text and is NOT `aria-hidden`, because
 * "there was an image here and it did not load" is exactly what a screen-reader user was missing: the
 * repaired `alt=""` makes the dead `<img>` decorative, so without this the failure is announced as
 * nothing at all. With no label passed the placeholder stays `aria-hidden` and glyph-only, which is
 * the pre-2026-08-22 rendering — so a caller that cannot localize never regresses.
 *
 * @returns how many images were newly given a placeholder on this pass
 */
export function enhanceBodyImageFallbacks(root: HTMLElement | null | undefined,
                                          unavailableLabel?: string | null): number {
    if (!root) {
        return 0;
    }

    let replaced = 0;

    root.querySelectorAll("img").forEach((node) => {
        const image = node as HTMLImageElement;

        repairLiteralNullAttributes(image);

        const applyIfFailed = () => {
            if (applyBodyImageFallback(image, unavailableLabel)) {
                replaced += 1;
            }
        };

        if (!errorBoundImages.has(image)) {
            errorBoundImages.add(image);
            image.addEventListener("error", applyIfFailed);
        }

        // `complete` with a zero natural width is a finished, failed load — not a slow one.
        if (image.complete && image.naturalWidth === 0) {
            applyIfFailed();
        }
    });

    return replaced;
}

function repairLiteralNullAttributes(image: HTMLImageElement): void {
    if (LITERAL_NULL_ATTRIBUTE_VALUES.has(image.getAttribute("alt") ?? "")) {
        // Emptied, not removed: an image with no `alt` at all falls back to announcing its filename.
        image.setAttribute("alt", "");
    }
    (["title", "width", "height"] as const).forEach((attribute) => {
        if (LITERAL_NULL_ATTRIBUTE_VALUES.has(image.getAttribute(attribute) ?? "")) {
            image.removeAttribute(attribute);
        }
    });
}

/** @returns true when this call is the one that inserted the placeholder */
function applyBodyImageFallback(image: HTMLImageElement, unavailableLabel?: string | null): boolean {
    if (failedImages.has(image)) {
        return false;
    }
    failedImages.add(image);
    image.setAttribute(BODY_IMAGE_FAILED_ATTRIBUTE, "true");

    const figure = image.closest("figure");
    figure?.setAttribute(BODY_IMAGE_FAILED_ATTRIBUTE, "true");

    // The image may already be inside the permalink anchor from a previous pass; the placeholder goes
    // outside it, so the dead box is not a link target either.
    const anchored = image.parentElement?.hasAttribute(MEDIA_PERMALINK_ANCHOR_ATTRIBUTE)
        ? image.parentElement
        : image;
    const insertionParent = anchored.parentNode;
    if (!insertionParent) {
        return false;
    }

    const label = unavailableLabel?.trim();

    const placeholder = image.ownerDocument.createElement("div");
    placeholder.setAttribute(BODY_IMAGE_FALLBACK_ATTRIBUTE, "true");
    placeholder.setAttribute("data-playwright-id", "body-image-fallback");
    if (!label) {
        // No label to read: keep the pre-2026-08-22 rendering rather than announcing an empty box.
        placeholder.setAttribute("aria-hidden", "true");
    }
    placeholder.className = BODY_IMAGE_FALLBACK_CLASS;
    placeholder.innerHTML = BODY_IMAGE_FALLBACK_GLYPH;
    if (label) {
        // `textContent`, never `innerHTML`: this string comes from a catalog today, and the one thing
        // that must stay true of this module is that it can never inject markup into a member's body.
        const caption = image.ownerDocument.createElement("span");
        caption.className = BODY_IMAGE_FALLBACK_LABEL_CLASS;
        caption.textContent = label;
        placeholder.appendChild(caption);
    }
    insertionParent.insertBefore(placeholder, anchored);

    // The node is untouched except for this: the browser has already failed to paint it, so all that
    // is suppressed is the torn-page chrome. `src`, `alt` and the element itself stay in the DOM.
    image.style.display = "none";
    return true;
}

/**
 * @returns how many images were newly wrapped (0 on a re-run over already-enhanced content)
 */
export function enhanceBodyImagePermalinks(root: HTMLElement | null | undefined,
                                           hint?: MediaPermalinkHint | null,
                                           unavailableLabel?: string | null): number {
    if (!root) {
        return 0;
    }

    // Failed images first, so the loop below can skip wrapping a dead box in a link to a media page
    // that would fail the same way. It is its own exported function purely so it can be tested alone;
    // it runs from here because this is the seam the host already calls on every render.
    enhanceBodyImageFallbacks(root, unavailableLabel);

    let wrapped = 0;

    // querySelectorAll returns a STATIC list, so moving nodes into new parents below cannot disturb it.
    root.querySelectorAll("img[src]").forEach((image) => {
        // A provably failed asset has no useful permalink: /media/{id} renders the same asset.
        if (image.hasAttribute(BODY_IMAGE_FAILED_ATTRIBUTE)) {
            return;
        }

        // Skip any image that is already an anchor descendant. This is doing two jobs at once:
        //
        //   1. Idempotency. The discussion route re-mounts, so this runs again over content we already
        //      enhanced; the image is by then inside OUR anchor, so it is skipped and no anchor ever
        //      nests inside another.
        //   2. Never nest inside an EXISTING anchor. An author can hand-link an image, and body HTML
        //      also carries TipTap LinkCard and ArgumentEmbed nodes whose images are already anchor
        //      descendants. <a> inside <a> is invalid HTML and the browser would silently restructure
        //      the DOM around it.
        //
        // These two are the same check, which is why it is one line and not two.
        if (image.closest("a")) {
            return;
        }

        // A clip poster is not an image permalink target, and wrapping one would break the clip twice
        // over: /media/{id} renders a still IMAGE, so the "permalink for a video" would be a page
        // showing one frame of it, and the anchor would additionally swallow the play gesture before
        // the clip's own affordance ever saw it. Clips own their click; see bodyClipPlayers.
        if (image.hasAttribute(CLIP_MARKER_ATTRIBUTE)) {
            return;
        }

        // Only Seavien-hosted images have a permalink at all. External body images (remote URLs from
        // imported content) must be left completely untouched — they have no /media page.
        const reference = getUserOwnedImageReferenceFromUrl(image.getAttribute("src"));
        const href = mediaPermalink(reference?.imagePublicId, hint);
        if (!href) {
            return;
        }

        const anchor = image.ownerDocument.createElement("a");
        anchor.setAttribute("href", href);
        anchor.setAttribute(MEDIA_PERMALINK_ANCHOR_ATTRIBUTE, "true");
        anchor.setAttribute("data-playwright-id", "body-image-permalink");
        // Block, matching how the image already lays out under the preflight `img { display: block }`,
        // so wrapping introduces no inline-box gap. The image's OWN attributes are never touched.
        anchor.className = "seavien-media-permalink block";

        image.parentNode?.insertBefore(anchor, image);
        anchor.appendChild(image);
        wrapped += 1;
    });

    return wrapped;
}

/**
 * <b>The React binding lives in `@/main/react/media/useBodyMediaLightbox`, not here.</b>
 *
 * <p>This module used to export a `useBodyImagePermalinks` hook that ran the enhancement above in an
 * effect keyed on `[kind, contentId, contentHtml]`. It was removed on 2026-08-01 when the lightbox
 * subsumed it, and the deps are the reason it is not worth reviving: React re-applies
 * `dangerouslySetInnerHTML` whenever the host re-renders with a fresh `{__html}` object, which destroys
 * every anchor this function wrapped — so an effect that re-runs only when the HTML *changes* silently
 * lost a body's image links on the first re-render that had any other cause. `useBodyMediaLightbox`
 * re-runs the enhancement on every render instead (it is idempotent and skips already-wrapped images).
 *
 * <p>Attach whatever ref you use to the body container specifically, never to a wrapper that also
 * contains chrome: an author avatar caught by this would start linking to the image instead of the
 * profile, which is a straight UX regression (design §9.3).
 */
